import { frameKey, isInterruptingFrame } from '@/shared/domain/notificationFrames';
import { NOTICE_FACT_LINE_KEYS, noticeFactsOf, noticeTitle } from '@/shared/domain/noticeFacts';
import { platformReasonKey } from '@/shared/domain/reasonVocabulary';
import {
  formatSteamCode,
  steamCodeAlreadyShown,
  steamCodeDetail,
} from '@/shared/domain/steamDetail';
import type { EventFrame } from './types';
export type NoticeTier = 'act' | 'done' | 'info' | 'failed';
/**
 * 通知的**配色档** —— 与 tier 分开的一条轴。
 *
 * tier 决定「弹多久 / 什么图标」，tone 决定「什么颜色」。分开是因为两者不是
 * 一一对应：IMPORTANT 通知与 IMPORTANT 广播都是 `act` 档（要人看见 ⇒ 琥珀），
 * 而 `failed` 档（红）留给「读错了会出事」的那类事实。
 *
 * ⚠️ **当前没有规则产出 `failed` 档**：兑换失败不弹浮层（用户裁定 2026-10-23
 * ⇒ 打断集 = 目录 `notice: act` 的那几帧，`redeem.card_processed` 不在其中）⇒
 * 红色这一档**预留未用**（调色板是产品决定，不因暂时没有用例就改）。
 *
 * 颜色取 antd 的语义 token（`colorWarning` / `colorError` / `colorSuccess` /
 * `colorInfo`）⇒ 跟主题走（明暗两套都正确），不自造颜色。
 */
export type NoticeTone = 'warning' | 'error' | 'success' | 'info';
/**
 * 提示正文的一行。
 *
 * 两种形态：**带 i18n 键的行**（有标签，如「订单：ORD-1」）与**纯文本行**
 * （运行时值，不翻译，如广播正文、原始 Steam 码）。
 */
export type NoticeLine =
  | { key: string; args?: Readonly<Record<string, string | number>>; subtle?: boolean }
  | { text: string; subtle?: boolean };
export interface Notice {
  tier: NoticeTier;
  /** 来源帧（分类）——铃铛帧那一路是**触发这条通知的业务帧名**。 */
  subKind: string;
  /** 分档（同一来源帧的不同事件）——聚合键的一段。 */
  grade: string;
  /** 主体（事件主角）——进正文药丸与格内登记；无主体为空串。 */
  subject: string;
  key: string;
  /**
   * 标题——**事件名**（帧 + 分档派生），无插值（用户裁定 2026-10-24：
   * 标题说「什么类型的事件」，主角进正文）。
   */
  titleKey: string;
  /** 详情行——订单 / 码 / 步骤 / 原始码（关系行已按用户裁定退出浮层）。 */
  lines: NoticeLine[];
  /** 事件时刻（信封 `ts`，ISO）——时间行用；缺省 null 不印。 */
  at: string | null;
}
/**
 * 档位 → 呈现（停留时长 + 配色档）。
 *
 * - `act`（要人动手/要看）：**琥珀**，停留最久（8s）——admin 点名的 IMPORTANT
 *   通知与广播走这一档：醒目但不刺眼。
 * - `failed`（失败）：**红**，停留 8s——失败要读，红是最不容易看错的档。
 * - `done`（办完了）：**绿**，4s——只是确认，不必久留。
 * - `info`（一般知会）：**蓝**，2s。
 */
export const TIER_PRESENT: Record<NoticeTier, { duration: number; tone: NoticeTone }> = {
  act: { duration: 8, tone: 'warning' },
  failed: { duration: 8, tone: 'error' },
  done: { duration: 4, tone: 'success' },
  info: { duration: 2, tone: 'info' },
};
type Payload = Record<string, unknown>;
function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}
type RuleNotice = Omit<Notice, 'key'>;
/**
 * **业务帧的统一静默档** —— 打断只走铃铛帧那一路（用户裁定 2026-10-22）。
 *
 * ⚠️ 一条规则顶十四帧：业务帧的处置**没有分档**（成功不打断、失败也不打断）⇒
 * 逐帧各写一条 `() => null` 只会漂成十四份同形的空规则 ⇒ 收成**一个**常量。
 */
const SILENT: (payload: Payload) => null = () => null;
/**
 * 帧 → 提示规则。
 *
 * **打断只走 notification 通道**（用户裁定 2026-10-22：「业务帧一律不弹
 * toast」＋「需要弹 toast 的，都会有 notification」；2026-10-23 追加裁定：
 * **弹的只有目录里 `notice: act` 的那几帧**，`redeem.card_processed` 不弹）⇒
 * 业务帧的规则恒返回 `null`：它们该落行的 `notice: act` 档由铃铛帧
 * `notification.created` 承担。
 *
 * 铃铛帧按**两道判据**弹（缺一不可）：
 *
 * ① 行的 `importance` 是 IMPORTANT（ADR 0006 §10「行 → 打断强度」：IMPORTANT ⇒
 *    角标 + 弹提示；NORMAL ⇒ 只进列表）——管**该不该打扰这个人**；
 * ② 来源帧在**打断集**里（`shared/domain/notificationFrames.ts::INTERRUPTING_FRAMES`
 *    = 目录 `notice: act` 的镜像）——管**这一类事实该不该用浮层打断**。
 *
 * ⇒ `redeem.card_processed` 的失败行**是** IMPORTANT（铃铛里看得见：重要行 +
 * 角标 +1），但它不在打断集里 ⇒ **不弹**（用户裁定 2026-10-23）。
 *
 * ⚠️ `redeem.expired` 是豁免帧（后端判定「不成帧」，`frontend: keep`）⇒
 * 目录里没有它 ⇒ 它既不会触发、也不会弹（与其他业务帧同档）。
 */
const RULES: Record<EventFrame, (payload: Payload) => RuleNotice | null> = {
  // 控制帧：慢消费者被摘——不改数据、不打断。
  'stream.reconnect': SILENT,
  // 业务帧：**一律静默**（成功不打断；失败也不打断——失败由铃铛行承担）。
  'account.online': SILENT,
  'account.need_code': SILENT,
  'account.failed': SILENT,
  'order.account.granted': SILENT,
  'order.account.revoked': SILENT,
  'order.account.deleted': SILENT,
  'cdkey.bound': SILENT,
  'redeem.attribute': SILENT,
  'fx.refresh_failed': SILENT,
  'fx.refreshed': SILENT,
  'proxy.check_speed_done': SILENT,
  'redeem.card_processed': SILENT,
  'redeem.expired': SILENT,
  // 通知域自己发的两帧：铃铛帧（按行 importance + 打断集）与广播帧（admin 公告）。
  'notification.broadcast': (p) => {
    // NORMAL 广播静默（只动铃铛）；IMPORTANT 广播是 admin 点名要人看见的，
    // 弹 act 档：标题是事件名「系统公告」（走 i18n），正文是广播
    // message（管理员运行时文本，与 username/订单号一样不翻译）。
    // importance/message/ts 读信封顶层键（后端 broadcast_notification）。
    if (p.importance !== 'IMPORTANT') return null;
    const message = str(p.message);
    if (message === null) return null;
    return {
      tier: 'act',
      subKind: 'important',
      // 公告**没有 id 也没有主体可聚合**，正文又各不相同 ⇒ 正文指纹当身份段
      // （每条公告各自一格，互不顶替；同文重发 ⇒ 同格计数，内容相同不算丢信息）。
      grade: announcementTag(message),
      subject: '',
      titleKey: frameKey('notification.broadcast'),
      lines: [{ text: message }],
      at: str(p.ts),
    };
  },
  'notification.created': (p) => {
    // 按**行的 importance** 分级（ADR 0006 §10「行 → 打断强度」）：
    // IMPORTANT ⇒ 角标 + 弹提示；NORMAL ⇒ 只进列表，不弹。
    // ⚠️ 这一帧是**通知域自己发的那一帧**（逐收件人一条，由 relay 投递）⇒
    // 载荷顶层带 `importance` / `frame` / `reason` 与**事实槽位**，与业务帧的
    // `data` 形状不同。
    if (p.importance !== 'IMPORTANT') return null;
    const frame = str(p.frame);
    if (frame === null) return null;
    // ⚠️ **打断集**判据（用户裁定 2026-10-23）：来源帧不在打断集里 ⇒ 不弹。
    // 典型：兑换失败（`redeem.card_processed`）——它在铃铛里是重要行，但不用
    // 浮层打断。
    if (!isInterruptingFrame(frame)) return null;
    // 事实（主体 / 订单号 / 阶段 / 动作 / 码）——标题分档与正文都由它派生。
    const facts = noticeFactsOf(p);
    const title = noticeTitle(frame, facts);
    // 铃铛帧的 `data` 带业务载荷——原始 Steam 码值就在里面。
    const detail = steamCodeDetail(p);
    // ⚠️ guard_type **不进浮层**（用户裁定 2026-10-24）：等码档它与档位一一对应
    // （2↔30 / 3↔32，标题已说「哪种码」）；失败档「哪种确认」由失败码的人话
    // 说出（`guard_device_confirm` ⇒「需到 Steam App 确认登录」）——数字没有
    // 增量。⇒ 协议小字只印 **EResult 形**（`Steam#N NAME`），没有 EResult 就不印。
    const suffix =
      detail !== null && detail.steam_eresult !== undefined
        ? formatSteamCode({
            steam_eresult: detail.steam_eresult,
            ...(detail.steam_eresult_name !== undefined
              ? { steam_eresult_name: detail.steam_eresult_name }
              : {}),
          })
        : null;
    // ⚠️ 「步骤」行已移除（用户裁定 2026-10-24）：step 是协议内部步骤名
    // （BeginAuthSession / jwt/ajaxrefresh …），对操作员是噪声；载荷里仍带
    // （`step` 槽位照发），只是浮层不印。
    const lines: NoticeLine[] = [];
    // ⚠️ `cdkey.bound` 的**主体是卡**（用户裁定 2026-10-24：后果落在卡的资产上，
    // 账号只是「绑到哪个账号」的事实）⇒ 显示上主体行第一：CDKey → 账号 → 订单号；
    // 主体槽位（subject）置空，账号退为事实行。
    if (facts.cdkey !== null) {
      lines.push({ key: NOTICE_FACT_LINE_KEYS.cdkey, args: { cdkey: facts.cdkey } });
      if (facts.subject !== null) {
        lines.push({ key: NOTICE_FACT_LINE_KEYS.account, args: { name: facts.subject } });
      }
    }
    if (facts.orderNo !== null) {
      lines.push({ key: NOTICE_FACT_LINE_KEYS.order, args: { order: facts.orderNo } });
    }
    if (facts.code !== null) {
      // ⚠️ **失败码 ⇒ 人话**（ADR 0011 §4）：
      // - 查得到平台词表 ⇒ 印**人话**（「验证码无效」而不是 `guard_code_invalid`）；
      // - 查不到 ⇒ 印**原样**（未映射的 Steam 官方名——「原样就是详情」，见
      //   `reasonVocabulary.ts` 的口径）。
      const phraseKey = platformReasonKey(facts.code);
      if (phraseKey !== null) {
        // 标题**已经说过**这个码 ⇒ 不重复印（等码档：「等待邮箱验证码」）。
        if (facts.code !== title.coveredCode) {
          lines.push({ key: phraseKey });
        }
      } else if (!steamCodeAlreadyShown(detail, facts.code)) {
        // EResult 行已经印了同一个官方名 ⇒ 不重复印（否则同一串名字出现两次）。
        lines.push({ key: NOTICE_FACT_LINE_KEYS.code, args: { code: facts.code } });
      }
    }
    if (suffix !== null) {
      lines.push({ text: suffix, subtle: true });
    }
    // ⚠️ 关系档位（waiter/anchor/…「你发起的」）**不进浮层**（用户裁定
    // 2026-10-24：浮层只说事件本身，不指向「你」）——它留在通知列表里。
    return {
      tier: 'act',
      subKind: frame,
      grade: title.grade,
      // cdkey.bound 的主体是卡 ⇒ 账号不占主体槽位（见上面的行序裁定）。
      subject: facts.cdkey !== null ? '' : facts.subject ?? '',
      titleKey: title.key,
      lines,
      at: str(p.ts),
    };
  },
};
/**
 * 公告正文的指纹（FNV-1a 32 位）——公告身份段用。
 *
 * ⚠️ 帧载荷没有公告 id（schema 只有 ts/importance/message）⇒ 只能拿正文当
 * 身份；取指纹而不是原文，键不至于随公告长度膨胀。
 */
function announcementTag(message: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < message.length; i += 1) {
    hash ^= message.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}
/**
 * 聚合键——**事件的标识**（同键 ⇒ antd 更新同一格）。
 *
 * ## 粒度是「来源帧 + 分档」＝ 一个**事件**
 *
 * - **来源帧**（`subKind`）：不同类的事实不混在一格里；
 * - **分档**（`grade`）：同一帧的不同**事件**（等邮箱码 ≠ 等令牌码；
 *   保活失败 ≠ 登录失败）；没有更细分档时为空串。
 *
 * ⚠️ **主体不在键里**（用户裁定 2026-10-24）：标题只说事件、不说主角 ⇒
 * 一格可装**多个主角**（药丸列表 + 计数承载）⇒ 一场多账号的风暴是
 * **1 格 + 计数**，不再被拆成一账号一格把 `maxCount` 吃光。
 * ⇒ 格内「哪个主角」由 `NoticeCell.subjects` 登记（见 `bumpCell`）。
 */
export function noticeKey(envelopeType: string, notice: RuleNotice): string {
  const parts = ['notice', envelopeType, notice.subKind];
  if (notice.grade !== '') parts.push(notice.grade);
  return parts.join(':');
}
export function selectNotice(frame: string, raw: unknown): Notice | null {
  const rule = RULES[frame as EventFrame];
  if (rule === undefined) return null;
  if (typeof raw !== 'object' || raw === null) return null;
  const notice = rule(raw as Payload);
  if (notice === null) return null;
  return { ...notice, key: noticeKey(frame, notice) };
}
/** 一格的聚合状态——「这格装了多少起、哪些主角、详情是否全体一致」。 */
export interface NoticeCell {
  /** 事件总数（含同账号重复）——标题计数 ×N 印的就是它。 */
  total: number;
  /** 最近一次事件的墙钟——窗口判定用。 */
  at: number;
  /** 各主角的事件数——Map 保插入序 ⇒ 药丸按首见顺序稳定排列。 */
  subjects: Map<string, number>;
  /**
   * 详情行（多主角时**全体一致才印**：风暴常是同一个失败码，那行值得保留；
   * 已出现分歧 ⇒ null ⇒ 不印，逐账号细节在通知列表里）。
   */
  uniformLines: NoticeLine[] | null;
}
/**
 * 格的聚合窗口：窗口内的事件算**同一波**（计数与主角列表不散架）；
 * 超窗再来的算新一波（归零重数）。取 30s——比 act 档时长（8s）长一截，
 * 一次批量操作的帧陆续到达时列表不会中途散架。
 */
export const CELL_WINDOW_MS = 30_000;
export function bumpCell(
  cells: Map<string, NoticeCell>,
  key: string,
  subject: string,
  lines: NoticeLine[],
  now: number,
): NoticeCell {
  const prev = cells.get(key);
  if (prev === undefined || now - prev.at > CELL_WINDOW_MS) {
    const subjects = new Map<string, number>();
    if (subject !== '') subjects.set(subject, 1);
    const cell: NoticeCell = { total: 1, at: now, subjects, uniformLines: lines };
    cells.set(key, cell);
    return cell;
  }
  prev.total += 1;
  prev.at = now;
  if (subject !== '') prev.subjects.set(subject, (prev.subjects.get(subject) ?? 0) + 1);
  let uniformLines: NoticeLine[] | null;
  if (prev.uniformLines === null) {
    uniformLines = null;
  } else {
    uniformLines =
      JSON.stringify(prev.uniformLines) === JSON.stringify(lines) ? prev.uniformLines : null;
  }
  prev.uniformLines = uniformLines;
  return prev;
}
